import React, { useMemo } from "react";
import { Image as ImageIcon, RefreshCcw, Info } from "lucide-react";

// createdList: [{ id, createdAt, title? }]

export default function CreditUsagePanel({
  width = 1200,
  credit = 0,
  createdList = 0,
  regenList = 0,
  loading = false
}) {
  const stats = useMemo(() => {
    const creates = createdList || 0;
    const regenerations = regenList || 0;

    // Rule: 1 create = 1 credit, 2 regenerations = 1 credit
    const regenCredits = regenerations / 2;
    const consumed = creates + regenCredits;

    // You can choose rounding style:
    // - If you actually deduct per 2 regen as a bundle, use Math.floor(regenerations/2)
    // - If you want to show fractional usage, keep /2
    const consumedRounded = Math.round(consumed * 100) / 100;

    // If you have "available credits", total capacity is consumed + available
    const total = consumedRounded + (Number(credit) || 0);
    const pct = total > 0 ? Math.min(100, Math.max(0, (consumedRounded / total) * 100)) : 0;

    return { creates, regenerations, regenCredits, consumedRounded, total, pct };
  }, [createdList, regenList, credit]);

  // Build a merged “recent activity” list


  const wrap = {
    width: 0.73 * width,
    margin: "16px auto 0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  };

  const card = {
    backgroundColor: "#0B0F14",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
  };

  const titleRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  };

  const title = {
    color: "white",
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: "flex",
    alignItems: "center",
    gap: 10,
  };

  const pill = {
    height: 34,
    padding: "0 12px",
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "white",
    background: "linear-gradient(90deg, #EE4037, #F7941D)",
    fontWeight: 800,
    fontSize: 13,
  };

  const rulesGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 12,
  };

  const ruleBox = {
    backgroundColor: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: 12,
    display: "flex",
    gap: 10,
    alignItems: "center",
  };

  const ruleText = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  };

  const ruleTop = { color: "white", fontWeight: 800, fontSize: 14, textAlign: 'left' };
  const ruleSub = { color: "rgba(255,255,255,0.72)", fontWeight: 600, fontSize: 12 };

  const statsRow = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 12,
    marginBottom: 12,
  };

  const statBox = {
    backgroundColor: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: 12,
  };

  const statLabel = { color: "rgba(255,255,255,0.72)", fontWeight: 700, fontSize: 12 };
  const statValue = { color: "white", fontWeight: 900, fontSize: 22, marginTop: 6 };

  const barWrap = {
    height: 14,
    width: "100%",
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.08)",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.10)",
  };

  const barFill = {
    height: "100%",
    width: `${stats.pct}%`,
    background: "linear-gradient(90deg, #EE4037, #F7941D)",
    borderRadius: 999,
    transition: "width 220ms ease",
  };

  const smallRow = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 10,
    gap: 12,
    color: "rgba(255,255,255,0.80)",
    fontSize: 12,
    fontWeight: 700,
  };

  const listWrap = {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  };

  const item = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
  };

  const left = { display: "flex", alignItems: "center", gap: 10, minWidth: 0 };
  const iconBubble = (kind) => ({
    width: 34,
    height: 34,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      kind === "create"
        ? "rgba(247,148,29,0.18)"
        : "rgba(238,64,55,0.18)",
    border:
      kind === "create"
        ? "1px solid rgba(247,148,29,0.35)"
        : "1px solid rgba(238,64,55,0.35)",
    flexShrink: 0,
  });

  const itemTitle = {
    color: "white",
    fontWeight: 800,
    fontSize: 13,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const itemSub = { color: "rgba(255,255,255,0.65)", fontWeight: 700, fontSize: 11, marginTop: 3 };

  const badge = (kind) => ({
    height: 26,
    padding: "0 10px",
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    fontWeight: 900,
    fontSize: 12,
    color: "white",
    background:
      kind === "create"
        ? "rgba(247,148,29,0.22)"
        : "rgba(238,64,55,0.22)",
    border:
      kind === "create"
        ? "1px solid rgba(247,148,29,0.35)"
        : "1px solid rgba(238,64,55,0.35)",
    flexShrink: 0,
  });

  // ✅ Skeleton shimmer (inline)
  const shimmerWrap = {
    position: "relative",
    overflow: "hidden",
    borderRadius: 14,
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const shimmerBar = {
    position: "absolute",
    top: 0,
    left: "-60%",
    height: "100%",
    width: "60%",
    background: "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.10), rgba(255,255,255,0))",
    animation: "shimmer 1.2s infinite",
  };

  const Skel = ({ h = 12, w = "100%", r = 10, mt = 0 }) => (
    <div style={{ ...shimmerWrap, height: h, width: w, borderRadius: r, marginTop: mt }}>
      <div style={shimmerBar} />
    </div>
  );


  return (
    loading ? <div style={wrap}>
      <div style={card}>
        <div style={titleRow}>
          <div style={title}>
            <Info size={18} color="#F7941D" />
            Credit Usage
          </div>

          <div style={pill}>
            <span style={{ fontWeight: 900 }}>{credit}</span>
            <span style={{ opacity: 0.95 }}>Credits available</span>
          </div>
        </div>

        {/* Rules */}
        <div style={rulesGrid}>
          <div style={ruleBox}>
            <div style={iconBubble("create")}>
              <ImageIcon size={18} color="#F7941D" />
            </div>
            <div style={ruleText}>
              <div style={ruleTop}>Image creation</div>
              <div style={ruleSub}>Costs <b>1 credit</b> per image</div>
            </div>
          </div>

          <div style={ruleBox}>
            <div style={iconBubble("regen")}>
              <RefreshCcw size={18} color="#EE4037" />
            </div>
            <div style={ruleText}>
              <div style={ruleTop}>Regeneration</div>
              <div style={ruleSub}>Costs <b>1 credit</b> per <b>2</b> regenerations</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={statsRow}>
          <div style={statBox}>
            <div style={statLabel}>Created</div>
            <div style={statValue}>{stats.creates}</div>
          </div>

          <div style={statBox}>
            <div style={statLabel}>Regenerations</div>
            <div style={statValue}>{stats.regenerations}</div>
          </div>

          <div style={statBox}>
            <div style={statLabel}>Credits used</div>
            <div style={statValue}>{stats.consumedRounded}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={barWrap}>
          <div style={barFill} />
        </div>

        <div style={smallRow}>
          <div>Used: <span style={{ color: "white" }}>{stats.consumedRounded}</span></div>
          <div>Total: <span style={{ color: "white" }}>{stats.total}</span></div>
        </div>



      </div>
    </div> : <div style={wrap}>
      <div style={card}>
        {/* keyframes inline */}
        <style>
          {`
              @keyframes shimmer {
                0% { transform: translateX(0); }
                100% { transform: translateX(220%); }
              }
            `}
        </style>

        <div style={titleRow}>
          <div style={title}>
            <Info size={18} color="#F7941D" />
            Credit Usage
          </div>
          <div style={{ ...pill, opacity: 0.65 }}>Loading...</div>
        </div>

        <div style={rulesGrid}>
          <div style={ruleBox}>
            <div style={iconBubble("create")} />
            <div style={{ width: "100%" }}>
              <Skel h={14} w="55%" r={999} />
              <Skel h={10} w="78%" r={999} mt={8} />
            </div>
          </div>

          <div style={ruleBox}>
            <div style={iconBubble("regen")} />
            <div style={{ width: "100%" }}>
              <Skel h={14} w="55%" r={999} />
              <Skel h={10} w="78%" r={999} mt={8} />
            </div>
          </div>
        </div>

        <div style={statsRow}>
          <div style={statBox}>
            <Skel h={10} w="45%" r={999} />
            <Skel h={22} w="35%" r={10} mt={10} />
          </div>
          <div style={statBox}>
            <Skel h={10} w="55%" r={999} />
            <Skel h={22} w="35%" r={10} mt={10} />
          </div>
          <div style={statBox}>
            <Skel h={10} w="55%" r={999} />
            <Skel h={22} w="35%" r={10} mt={10} />
          </div>
        </div>

        <Skel h={14} w="100%" r={999} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
          <Skel h={10} w="20%" r={999} />
          <Skel h={10} w="20%" r={999} />
        </div>

       
      </div>
    </div>
  );
}
